import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SpinnerGap, Check } from "@phosphor-icons/react";
import axios from "axios";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAIL_CLUB_VECTOR = "https://customer-assets.emergentagent.com/job_vandys-gallery/artifacts/kli0ghmk_Untitled_Artwork%206.png";

function InteractiveMailCharacter() {
  const [animating, setAnimating] = useState(false);
  const containerRef = useRef(null);
  const charRef = useRef(null);
  const frameRef = useRef(null);
  const startTimeRef = useRef(0);

  const startAnimation = useCallback(() => {
    if (animating) return;
    setAnimating(true);
    startTimeRef.current = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      if (elapsed >= 2) {
        // Reset to center
        if (charRef.current) {
          charRef.current.style.transform = "translateX(0) rotate(0deg) scaleX(1) scaleY(1)";
        }
        setAnimating(false);
        return;
      }

      if (!charRef.current) return;

      // Running back and forth
      const runCycle = elapsed * 3; // faster run
      const direction = Math.floor(runCycle) % 2 === 0 ? 1 : -1;
      const progress = (runCycle % 1);
      const xPos = direction === 1 
        ? -150 + progress * 300 
        : 150 - progress * 300;
      
      // Waving board - raising hands up and down
      const wave = Math.sin(elapsed * 10) * 15;
      // Bouncing while running
      const bounce = Math.abs(Math.sin(elapsed * 8)) * 18;
      // Squash and stretch for liveliness (eyes open/close feel)
      const squash = 1 + Math.sin(elapsed * 12) * 0.04;
      const stretch = 1 - Math.sin(elapsed * 12) * 0.03;
      // Raising arms effect via vertical scale pulse
      const raiseArms = Math.sin(elapsed * 6) * 5;
      // Flip direction
      const scaleX = direction === 1 ? 1 : -1;
      
      charRef.current.style.transform = `translateX(${xPos}px) translateY(${-bounce + raiseArms}px) rotate(${wave}deg) scaleX(${scaleX * squash}) scaleY(${stretch})`;

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);

    setTimeout(() => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    }, 4100);
  }, [animating]);

  useEffect(() => {
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center py-4 md:py-6 cursor-pointer"
      onClick={startAnimation}
      data-testid="mail-club-character"
    >
      {/* Click hint */}
      {!animating && (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-[#E01A23] opacity-70 animate-pulse" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
          click me!
        </div>
      )}
      <div
        ref={charRef}
        className="transition-none"
        style={{ willChange: "transform" }}
      >
        <img
          src={MAIL_CLUB_VECTOR}
          alt="Vandy's Mail Club"
          className="w-[336px] sm:w-[432px] md:w-[576px] h-auto select-none"
          draggable={false}
          style={{ mixBlendMode: "multiply" }}
        />
      </div>
      {animating && (
        <div className="absolute inset-0 pointer-events-none">
          {/* Running dust particles */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute bottom-4 w-2 h-2 rounded-full bg-[#D4C470] opacity-50"
              style={{
                left: `${30 + Math.random() * 40}%`,
                animation: `starFloat 1s ease-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FloatingProduct({ src, style }) {
  const ref = useRef(null);
  const [phase] = useState(() => Math.random() * Math.PI * 2);
  const [speed] = useState(() => 0.3 + Math.random() * 0.4);

  useEffect(() => {
    let frame;
    const animate = () => {
      if (!ref.current) return;
      const t = Date.now() / 1000;
      const y = Math.sin(t * speed + phase) * 15;
      const x = Math.cos(t * speed * 0.6 + phase) * 8;
      const rot = Math.sin(t * speed * 0.4 + phase) * 5;
      ref.current.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [speed, phase]);

  return (
    <div ref={ref} className="absolute pointer-events-none hidden sm:block" style={style}>
      <div className="w-16 h-16 md:w-24 md:h-24 rounded-xl overflow-hidden shadow-lg opacity-60">
        <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
      </div>
    </div>
  );
}

export default function MailClubPage() {
  const [products, setProducts] = useState([]);
  const [digitalEmail, setDigitalEmail] = useState("");
  const [digitalName, setDigitalName] = useState("");
  const [digitalStatus, setDigitalStatus] = useState(null);
  const [postalForm, setPostalForm] = useState({
    email: "", name: "", address_line1: "", address_line2: "",
    city: "", state: "", zip_code: "", country: "US", plan: "monthly",
  });
  const [postalLoading, setPostalLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const handleDigitalSubmit = async (e) => {
    e.preventDefault();
    if (!digitalEmail) return;
    setDigitalStatus("loading");
    try {
      await axios.post(`${API}/mail-club/digital`, { email: digitalEmail, name: digitalName });
      setDigitalStatus("success");
    } catch { setDigitalStatus("error"); }
  };

  const handlePostalSubmit = async (e) => {
    e.preventDefault();
    setPostalLoading(true);
    try {
      const res = await axios.post(`${API}/mail-club/postal`, postalForm);
      if (res.data.order_id) {
        window.location.href = `/order/${res.data.order_id}`;
      }
    } catch { alert("Something went wrong."); } finally { setPostalLoading(false); }
  };

  const updatePostal = (f, v) => setPostalForm((p) => ({ ...p, [f]: v }));

  // Position floating products around the page edges
  const floatPositions = [
    { top: "5%", left: "3%" }, { top: "15%", right: "4%" },
    { top: "35%", left: "2%" }, { top: "50%", right: "3%" },
    { top: "65%", left: "4%" }, { top: "80%", right: "5%" },
    { bottom: "15%", left: "3%" }, { bottom: "5%", right: "4%" },
  ];

  return (
    <div data-testid="mail-club-page" className="pt-20 md:pt-24 min-h-screen relative overflow-hidden">

      {/* Floating product images around edges */}
      {products.slice(0, 8).map((p, i) => (
        <FloatingProduct key={p.id} src={p.image} style={floatPositions[i] || {}} />
      ))}

      <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Interactive mail club character */}
        <InteractiveMailCharacter />
        <p
          className="text-sm text-[#3a3a3a] max-w-md mx-auto text-center mb-6"
          style={{ fontFamily: "'Courier New', Courier, monospace" }}
        >
          a passion project — original art concepts delivered to your inbox or your doorstep, on the 14th & 28th of every month
        </p>

        {/* ═══ DIGITAL — FREE ═══ */}
        <div className="bg-[#EBD988] rounded-2xl p-8 md:p-10 mb-6" data-testid="digital-signup">
          <h2 className="text-2xl text-[#1A1A1A] mb-1" style={{ fontFamily: "'Lazy Dog', cursive" }}>digital mail club</h2>
          <p className="text-sm text-[#E01A23] font-medium mb-4" style={{ fontFamily: "'Courier New', Courier, monospace" }}>free</p>
          <p className="text-sm text-[#3a3a3a] mb-6" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            art concepts, behind-the-scenes sketches, and creative inspiration — twice a month, always free
          </p>

          {digitalStatus === "success" ? (
            <div className="flex items-center gap-3 text-green-600 bg-green-100 rounded-xl p-4">
              <Check size={24} weight="bold" />
              <p style={{ fontFamily: "'Courier New', Courier, monospace" }}>you're in! welcome to the mail club</p>
            </div>
          ) : (
            <form onSubmit={handleDigitalSubmit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input data-testid="digital-name" placeholder="your name (optional)" value={digitalName} onChange={(e) => setDigitalName(e.target.value)} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                <Input data-testid="digital-email" type="email" placeholder="your email" value={digitalEmail} onChange={(e) => setDigitalEmail(e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
              </div>
              <Button data-testid="digital-subscribe-btn" type="submit" disabled={digitalStatus === "loading"} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-8 text-sm">
                {digitalStatus === "loading" ? <><SpinnerGap size={16} className="animate-spin mr-2" /> subscribing...</> : "subscribe — it's free"}
              </Button>
            </form>
          )}
        </div>

        {/* ═══ POSTAL — PAID ═══ */}
        <div className="bg-[#EBD988] rounded-2xl p-8 md:p-10 mb-12" data-testid="postal-signup">
          <h2 className="text-2xl text-[#1A1A1A] mb-1" style={{ fontFamily: "'Lazy Dog', cursive" }}>postal mail club</h2>
          <p className="text-sm text-[#E01A23] font-medium mb-4" style={{ fontFamily: "'Courier New', Courier, monospace" }}>$5/month or $50/year (save $10)</p>
          <p className="text-sm text-[#3a3a3a] mb-6" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            real art delivered to your mailbox — hand-printed postcards, mini prints, stickers, and surprise goodies
          </p>

          <form onSubmit={handlePostalSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="postal-name" placeholder="full name" value={postalForm.name} onChange={(e) => updatePostal("name", e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
              <Input data-testid="postal-email" type="email" placeholder="email" value={postalForm.email} onChange={(e) => updatePostal("email", e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
            </div>
            <Input data-testid="postal-address1" placeholder="street address" value={postalForm.address_line1} onChange={(e) => updatePostal("address_line1", e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
            <Input data-testid="postal-address2" placeholder="apt, suite, etc. (optional)" value={postalForm.address_line2} onChange={(e) => updatePostal("address_line2", e.target.value)} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Input data-testid="postal-city" placeholder="city" value={postalForm.city} onChange={(e) => updatePostal("city", e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
              <Input data-testid="postal-state" placeholder="state" value={postalForm.state} onChange={(e) => updatePostal("state", e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
              <Input data-testid="postal-zip" placeholder="zip code" value={postalForm.zip_code} onChange={(e) => updatePostal("zip_code", e.target.value)} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
              <Input data-testid="postal-country" placeholder="country" value={postalForm.country} onChange={(e) => updatePostal("country", e.target.value)} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
            </div>

            <div className="flex gap-3" data-testid="plan-toggle">
              <button type="button" onClick={() => updatePostal("plan", "monthly")} className={`flex-1 p-4 rounded-xl border-2 transition-all text-center ${postalForm.plan === "monthly" ? "border-[#E01A23] bg-[#E01A23]/10" : "border-[#D4C470] bg-[#F5E6A0]"}`}>
                <p className="text-lg text-[#1A1A1A]" style={{ fontFamily: "'Lazy Dog', cursive" }}>$5/month</p>
                <p className="text-xs text-[#3a3a3a]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>billed monthly</p>
              </button>
              <button type="button" onClick={() => updatePostal("plan", "annual")} className={`flex-1 p-4 rounded-xl border-2 transition-all text-center relative ${postalForm.plan === "annual" ? "border-[#E01A23] bg-[#E01A23]/10" : "border-[#D4C470] bg-[#F5E6A0]"}`}>
                <span className="absolute -top-2 right-3 text-[10px] bg-[#E01A23] text-white px-2 py-0.5 rounded-full">save $10</span>
                <p className="text-lg text-[#1A1A1A]" style={{ fontFamily: "'Lazy Dog', cursive" }}>$50/year</p>
                <p className="text-xs text-[#3a3a3a]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>12 months of art</p>
              </button>
            </div>

            <Button data-testid="postal-subscribe-btn" type="submit" disabled={postalLoading} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-8 text-sm">
              {postalLoading ? <><SpinnerGap size={16} className="animate-spin mr-2" /> processing...</> : `subscribe — $${postalForm.plan === "monthly" ? "5" : "50"}`}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
